import { useMemo } from "react";
import { i18nCrossUtil } from "@adgm/libs";
import type { TDirector, TAuthorisedSignatory } from "@adgm/cross-models";

/**
 * 董事数据接口
 */
export interface IDirectorData extends TDirector {
  id?: string;
}

/**
 * 授权签字人数据接口
 */
export interface IAuthorisedSignatoryData extends TAuthorisedSignatory {
  id?: string;
}

/**
 * 组件 Props 接口
 */
export interface IKycDirectorsTableProps {
  value: (IDirectorData | IAuthorisedSignatoryData)[];
  onChange?: (data: (IDirectorData | IAuthorisedSignatoryData)[]) => void;
  /** 是否为授权签字人，默认为 false（董事） */
  isAuthorisedSignatory?: boolean;
}

/**
 * 列配置创建器类型
 */
export type TColumnConfigCreator<T = any> = (type: string, options?: any) => T;

/**
 * hook 返回值接口
 */
export interface IUseKycDirectorsTableReturn<TColumnConfig = any> {
  /** 当前数据（带ID） */
  directors: (IDirectorData | IAuthorisedSignatoryData)[];
  /** 列配置 */
  directorColumns: TColumnConfig[];
  /** 数据变化处理函数 */
  handleChange: (data: any[]) => void;
  /** 国际化对象 */
  crossIntl: any;
}

/**
 * KYC 董事/授权签字人表格公共逻辑 Hook
 *
 * @param props - 组件props
 * @param createRenderer - 渲染器创建函数
 * @returns 表格所需的所有逻辑和数据
 */
export function useKycDirectorsTable<TColumnConfig = any>(
  props: IKycDirectorsTableProps,
  createRenderer: TColumnConfigCreator,
): IUseKycDirectorsTableReturn<TColumnConfig> {
  const { value, onChange, isAuthorisedSignatory = false } = props;

  const crossIntl = i18nCrossUtil.t();

  // 根据类型获取国际化 key 前缀

  // 定义表格的列配置（新结构只有3列：name, nationalities, residentialAddress）
  const directorColumns = [
    {
      key: "name",
      title: isAuthorisedSignatory
        ? crossIntl["kyc.v2.display.label.authorised.signatory.name"]
        : crossIntl["kyc.v2.display.label.director.name"],
      dataIndex: "name",
      required: true,
      type: "text" as const,
      render: createRenderer("text", { maxLength: 500 }),
    },
    {
      key: "nationalities",
      title: crossIntl["kyc.v2.display.label.nationality"],
      dataIndex: "nationalities",
      required: true,
      type: "nationality" as const,
      render: createRenderer("nationality"),
    },
    {
      key: "residentialAddress",
      title: crossIntl["kyc.v2.display.label.residential.address"],
      dataIndex: "residentialAddress",
      required: true,
      type: "text" as const,
      render: createRenderer("text"),
    },
  ];

  const directorsValue = useMemo(() => {
    return value.map(item => {
      return {
        ...item,
      };
    });
  }, [value]);

  const handleChange = (data: any[]) => {
    // 转换回数据格式（新结构：name, nationalities, residentialAddress）
    const directors = data.map(item => {
      return {
        name: item.name || "",
        nationalities: item.nationalities || [],
        residentialAddress: item.residentialAddress || "",
      };
    });
    onChange?.(directors);
  };

  return {
    directors: directorsValue,
    directorColumns: directorColumns as TColumnConfig[],
    handleChange,
    crossIntl,
  };
}
